'use client'

import { createContext, useContext, useState, ReactNode } from 'react'

interface CartPopupContextType {
  anchorEl: HTMLElement | null
  anchorPosition: { top: number; left: number } | undefined
  isTouch: boolean
  setAnchorEl: (el: HTMLElement | null) => void
  setAnchorPosition: (position: { top: number; left: number } | undefined) => void
  setIsTouch: (isTouch: boolean) => void
  handleClose: () => void
  handleOpen: (event: React.MouseEvent<HTMLElement>) => void
}

const CartPopupContext = createContext<CartPopupContextType | undefined>(undefined)

export const CartPopupProvider = ({ children }: { children: ReactNode }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | undefined>(undefined)
  const [isTouch, setIsTouch] = useState(false)
  const [prevPosition, setPrevPosition] = useState<{ top: number; left: number } | undefined>(undefined)
  const handleClose = () => {
    setAnchorEl(null)
    setAnchorPosition(prevPosition)
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    
    if (isTouch) {
      // Toggle for touch devices
      if (anchorEl) {
        handleClose()
      } else {
        setAnchorEl(event.currentTarget)
        setAnchorPosition({
          top: rect.bottom + 8,
          left: rect.right,
        })
      }
    } else {
      setAnchorEl(event.currentTarget)
      setAnchorPosition({
        top: rect.bottom + 8,
        left: rect.right,
      })
    }
    setPrevPosition({ top: rect.bottom + 8, left: rect.right })
  }

  return (
    <CartPopupContext.Provider
      value={{
        anchorEl,
        anchorPosition,
        isTouch,
        setAnchorEl,
        setAnchorPosition,
        setIsTouch,
        handleClose,
        handleOpen,
      }}
    >
      {children}
    </CartPopupContext.Provider>
  )
}

export const useCartPopup = () => {
  const context = useContext(CartPopupContext)
  if (context === undefined) {
    throw new Error('useCartPopup must be used within a CartPopupProvider')
  }
  return context
}